import {
  Card,
  CrudMode,
  DataDisplay,
  formatDate,
  formatSchoolYear,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Select } from "components";
import {
  LkStudentDiplomaDisplayFieldId,
  StudentDiploma,
  StudentDiplomaEditResource,
  StudentDiplomaViewResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";

export const StudentDiplomaPageBody = (props: {
  crudMode: CrudMode;
  diploma: StudentDiploma;
  setDiploma: (value: StudentDiploma) => void;
  viewResource: StudentDiplomaViewResource;
  editResource: StudentDiplomaEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();

  const displayFields = props.viewResource.displayFields || [];
  const dateDisplayField = displayFields.find((x) => x.id === LkStudentDiplomaDisplayFieldId.Date);
  const degreeMajorDisplayField = displayFields.find((x) => x.id === LkStudentDiplomaDisplayFieldId.DegreeMajor);

  const yearField = () => {
    const propertyName = pn<StudentDiploma>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.diploma.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.diploma.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDiploma(
            produce(props.diploma, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const dateField = () => {
    const propertyName = pn<StudentDiploma>("date");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.diploma.date)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.diploma.date}
        onChange={(value) =>
          props.setDiploma(
            produce(props.diploma, (draft) => {
              draft.date = value;
            })
          )
        }
      />
    );
  };

  const diplomaTypeField = () => {
    const propertyName = pn<StudentDiploma>("diplomaTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.diploma.diplomaType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.diplomaTypes?.find((x) => x.id === props.diploma.diplomaTypeId)}
        options={props.editResource.diplomaTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDiploma(
            produce(props.diploma, (draft) => {
              draft.diplomaTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const degreeMajorField = () => {
    const propertyName = pn<StudentDiploma>("degreeMajorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.diploma.degreeMajor?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.degreeMajors?.find((x) => x.id === props.diploma.degreeMajorId)}
        options={props.editResource.degreeMajors}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDiploma(
            produce(props.diploma, (draft) => {
              draft.degreeMajorId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      {dateDisplayField && (
        <Grid item xs={smallFieldBreakpoints}>
          {dateField()}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {diplomaTypeField()}
      </Grid>

      {degreeMajorDisplayField && (
        <Grid item xs={smallFieldBreakpoints}>
          {degreeMajorField()}
        </Grid>
      )}
    </Grid>
  );
};
